import { getMap, TILE } from '@wahala/maps';
import { dist2 } from './geometry.js';
import { nextU32, seedRng } from './rng.js';
export const arrestConfig = (s) => s.config.arrest;
/** The deadline id that ends an arrest nobody tapped. */
export const arrestDeadline = (victim) => `arrest:${victim}`;
/** A tap may say it came this much earlier than it reached us (the phone's lag), never later. */
export const TAP_LAG_MS = 250;
/** Room over the measured lag for the phone's frame and a little jitter. */
export const TAP_SLACK_MS = 60;
/**
 * How far back this tap may reach: the worker's measured one-way lag for the connection plus the
 * slack, never past TAP_LAG_MS. Without a measure, the full allowance. A phone cannot choose a
 * moment of the needle further back than its own network allows.
 */
export function tapAllowance(lagMs) {
    if (lagMs === undefined || !Number.isFinite(lagMs) || lagMs < 0)
        return TAP_LAG_MS;
    return Math.min(TAP_LAG_MS, Math.round(lagMs) + TAP_SLACK_MS);
}
/** The needle's jitter jumps this often, and swings this far either way (a share of the bar). */
const JITTER_MS = 90;
const JITTER = 0.12;
export function createArrests(s) {
    return s.config.arrest
        ? { rng: seedRng((s.seed ^ 0x41525354) >>> 0), open: {}, jailed: {}, revealed: {}, cuts: {} }
        : undefined;
}
/** In an open arrest, as the driver or the VIO. */
export const inArrest = (s, id) => {
    const a = s.arrest;
    if (!a)
        return null;
    if (Object.hasOwn(a.open, id))
        return a.open[id];
    for (const v of Object.keys(a.open).sort())
        if (a.open[v].by === id)
            return a.open[v];
    return null;
};
export const jailed = (s, id) => s.arrest !== undefined && Object.hasOwn(s.arrest.jailed, id);
/** Locked: in an arrest or in the yard. Stands still and does nothing but wait (and tap the needle). */
export const locked = (s, id) => jailed(s, id) ? 'JAILED' : inArrest(s, id) ? 'ARRESTED' : null;
/** A value in [-1, 1] for this seed and step: the needle's jitter. */
function jit(seed, k) {
    const h = Math.imul((seed ^ Math.imul(k + 1, 0x9e3779b1)) >>> 0, 0x85ebca6b) >>> 0;
    const m = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
    return ((m ^ (m >>> 16)) % 2001) / 1000 - 1;
}
/**
 * Where the needle is, `t` ms into the arrest: 0 the bar's left end, 1 its right. A sweep there and
 * back (its pace from the seed), with a jitter that jumps every 90 ms and slides between jumps.
 */
export function needleAt(seed, t) {
    const period = 700 + (seed % 500);
    const ph = (Math.max(0, t) % period) / period;
    const sweep = ph < 0.5 ? ph * 2 : 2 - ph * 2;
    const k = Math.floor(Math.max(0, t) / JITTER_MS);
    const f = (Math.max(0, t) % JITTER_MS) / JITTER_MS;
    const j = jit(seed, k) + (jit(seed, k + 1) - jit(seed, k)) * f;
    return Math.min(1, Math.max(0, sweep + j * JITTER));
}
/** The zone a needle position falls in: the lawyer, the sweet spot, wahala (left to right). */
export function zoneOf(c, x) {
    if (x < c.lawyer)
        return 'lawyer';
    if (x < c.lawyer + c.sweet)
        return 'bribe';
    return 'yard';
}
/** The VIO presses Arrest on a driver in reach (validated as a knock): both lock, the needle starts. */
export function startArrest(s, p, target, events) {
    const a = s.arrest;
    const c = arrestConfig(s);
    for (const x of [p, target]) {
        x.input = { dx: 0, dy: 0 };
        x.vel = { x: 0, y: 0 };
        s.sessions[x.id] = null;
    }
    const pos = { ...target.pos };
    a.open[target.id] = { by: p.id, startedAt: s.now, endsAt: s.now + c.needleMs, needle: nextU32(a.rng), pos };
    s.deadlines[arrestDeadline(target.id)] = s.now + c.needleMs;
    p.killReadyAt = s.now + s.config.killCooldownMs;
    events.push({ t: 'arrest.started', by: p.id, playerId: target.id, pos });
}
export function checkTap(s, p) {
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    const a = s.arrest;
    if (!a || !Object.hasOwn(a.open, p.id))
        return 'NO_ARREST';
    return s.now <= a.open[p.id].endsAt ? null : 'NO_ARREST';
}
/**
 * The driver's tap: `at` ms into the needle as their phone saw it, taken when it is no later than
 * now and no more than the lag allowance earlier (otherwise now). The zone there decides.
 */
export function tapNeedle(s, p, at, events, lagMs) {
    const a = s.arrest;
    const open = a.open[p.id];
    const elapsed = s.now - open.startedAt;
    const t = at <= elapsed && at >= elapsed - tapAllowance(lagMs) ? at : elapsed;
    endArrest(s, p.id, zoneOf(arrestConfig(s), needleAt(open.needle, t)), events);
}
/** The officer's patience ran out with no tap: wahala. */
export function arrestTimedOut(s, victim, events) {
    if (s.arrest && Object.hasOwn(s.arrest.open, victim))
        endArrest(s, victim, 'yard', events);
}
/** Where the n-th driver locked up stands in the yard: in rows behind the gate. */
export function yardSpot(yard, n) {
    return { x: yard.x + ((n % 4) - 1.5) * 45, y: yard.y - 60 - Math.floor(n / 4) * 45 };
}
function endArrest(s, victim, outcome, events) {
    const a = s.arrest;
    const c = arrestConfig(s);
    const open = a.open[victim];
    delete a.open[victim];
    delete s.deadlines[arrestDeadline(victim)];
    const p = s.players[victim];
    let undone = 0;
    if (outcome === 'bribe') {
        // the bribe: a share of the stops done are undone (single-stage stops; a staged one keeps its place)
        const done = (s.tasks[victim] ?? []).filter((t) => t.done && (t.stages ?? 1) === 1);
        const n = Math.ceil(done.length * c.bribeShare);
        for (const t of done.slice(done.length - n))
            t.done = false;
        undone = n;
    }
    else if (outcome === 'yard') {
        const yard = getMap(s.mapId).stations.find((x) => x.kind === 'yard');
        // the keke clamped where it stood: a breakdown anyone can report
        s.bodies.push({
            id: victim,
            playerId: victim,
            pos: { ...open.pos },
            createdAt: s.now,
            reported: false,
            clamp: true,
        });
        a.jailed[victim] = s.now;
        const n = Object.keys(a.jailed).length - 1;
        p.pos = yardSpot(yard?.pos ?? getMap(s.mapId).meetingSpot, n);
        p.vel = { x: 0, y: 0 };
        const by = s.players[open.by];
        by.stats.kills++;
    }
    events.push({ t: 'arrest.ended', by: open.by, playerId: victim, outcome, undone });
}
/** A meeting: every open arrest ends with the driver let go, every cut stops, the yard is shown. */
export function stopArrests(s, events) {
    const a = s.arrest;
    if (!a)
        return;
    for (const v of Object.keys(a.open).sort())
        endArrest(s, v, 'released', events);
    a.cuts = {};
    for (const id of Object.keys(a.jailed).sort())
        a.revealed[id] = true;
}
/** The yard and its gate radius, or null where the map has none. */
function yardOf(s) {
    const yard = getMap(s.mapId).stations.find((x) => x.kind === 'yard');
    const c = arrestConfig(s);
    return yard && c ? { pos: yard.pos, r: c.yardRadius * TILE } : null;
}
export function checkCut(s, p) {
    const a = s.arrest;
    if (!a)
        return 'NOT_IN_MODE';
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    if (!p.alive)
        return 'NOT_ALIVE';
    const y = yardOf(s);
    if (!y || dist2(p.pos, y.pos) > y.r * y.r)
        return 'OUT_OF_RANGE';
    if (Object.keys(a.jailed).length === 0)
        return 'NOTHING_HERE';
    const cut = Object.hasOwn(a.cuts, p.id) ? a.cuts[p.id] : undefined;
    if (cut && s.now < cut.at + arrestConfig(s).cutMs)
        return 'TOO_SOON';
    return null;
}
/** One cut: the first starts the padlock, each after its hold goes on, the last frees the yard. */
export function cutPadlock(s, p, events) {
    const a = s.arrest;
    const c = arrestConfig(s);
    const cut = Object.hasOwn(a.cuts, p.id) ? a.cuts[p.id] : undefined;
    if (!cut) {
        a.cuts[p.id] = { stage: 0, at: s.now };
        events.push({ t: 'breakout.cut', playerId: p.id, stage: 0, of: c.cuts });
        return;
    }
    cut.stage++;
    cut.at = s.now;
    events.push({ t: 'breakout.cut', playerId: p.id, stage: cut.stage, of: c.cuts });
    if (cut.stage < c.cuts)
        return;
    delete a.cuts[p.id];
    // a VIO's cuts look the same and free nobody
    if (p.role !== 'crew')
        return;
    const freed = Object.keys(a.jailed).sort();
    const y = yardOf(s);
    freed.forEach((id, i) => {
        delete a.jailed[id];
        delete a.revealed[id];
        const f = s.players[id];
        // out of the gate in a row, back in their keke (the clamp comes off)
        if (y)
            f.pos = { x: y.pos.x + (i - (freed.length - 1) / 2) * 70, y: y.pos.y + 90 };
        f.vel = { x: 0, y: 0 };
    });
    s.bodies = s.bodies.filter((b) => !(b.clamp && freed.includes(b.playerId)));
    events.push({ t: 'breakout.freed', by: p.id, freed });
}
/** Each tick: a cutter who walks away from the gate (or is locked up) loses the cut. */
export function tickArrests(s) {
    const a = s.arrest;
    if (!a)
        return;
    const y = yardOf(s);
    for (const id of Object.keys(a.cuts).sort()) {
        const p = s.players[id];
        if (!y || !p.alive || locked(s, id) || dist2(p.pos, y.pos) > y.r * y.r)
            delete a.cuts[id];
    }
}
//# sourceMappingURL=arrest.js.map